import { useCallback, useEffect, useState } from 'react';
import { Globe, ExternalLink, RefreshCw, CheckCircle2, Copy, Check, Loader2 } from 'lucide-react';
import {
  addCustomDomain,
  checkCustomDomain,
  fetchHostingDomains,
  type CustomDomainStatus,
} from '../lib/nexusCustomDomain';

interface CustomDomainPanelProps {
  businessId: string;
}

/**
 * Custom domain management for a Nexus-hosted website (My Apps).
 *
 * Shows the always-working Vercel preview URL, then lets the owner attach
 * their own domain: we add it to the Vercel project and show the exact DNS
 * records to create at their registrar, with a "Check status" refresh until
 * the domain goes live.
 */
export default function CustomDomainPanel({ businessId }: CustomDomainPanelProps) {
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [domainStatus, setDomainStatus] = useState<CustomDomainStatus | null>(null);
  const [domainInput, setDomainInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const domains = await fetchHostingDomains(businessId);
      setPreviewUrl(domains.previewUrl);
      if (domains.customDomain) {
        // Existing custom domain — pull its live DNS/verification state.
        const status = await checkCustomDomain(businessId, domains.customDomain);
        setDomainStatus(status);
      } else {
        setDomainStatus(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load domain info');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleAddDomain = async () => {
    if (!domainInput.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const status = await addCustomDomain(businessId, domainInput.trim());
      setDomainStatus(status);
      setDomainInput('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add the domain');
    } finally {
      setBusy(false);
    }
  };

  const handleCheck = async () => {
    if (!domainStatus || checking) return;
    setChecking(true);
    setError(null);
    try {
      const status = await checkCustomDomain(businessId, domainStatus.name);
      setDomainStatus(status);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not check the domain');
    } finally {
      setChecking(false);
    }
  };

  const copy = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch { /* ignore */ }
  };

  const domainLive = !!domainStatus && domainStatus.verified && !domainStatus.misconfigured;

  return (
    <div className="space-y-4">
      <div className="border-t border-gray-200" />
      <div>
        <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Domains</p>

        {loading ? (
          <div className="flex items-center gap-2 py-3 text-sm text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading domain info…
          </div>
        ) : (
          <div className="space-y-3">
            {/* Preview URL — always works, from the moment hosting is connected */}
            {previewUrl && (
              <div className="flex flex-col gap-1.5 rounded-xl border border-gray-200 bg-gray-50/60 p-3.5">
                <p className="text-[12px] font-semibold text-gray-700">Preview URL</p>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 break-all min-w-0 font-medium"
                  >
                    {previewUrl.replace('https://', '')}
                    <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                  </a>
                  <span className="text-[11px] text-gray-400">Always available — use it to preview your site</span>
                </div>
              </div>
            )}

            {domainStatus ? (
              /* Custom domain exists — show status + DNS instructions until live */
              <div className={`rounded-xl border p-3.5 space-y-3 ${domainLive ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/40'}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <Globe className={`h-4 w-4 shrink-0 ${domainLive ? 'text-emerald-600' : 'text-amber-500'}`} />
                    <span className="text-sm font-semibold text-gray-900 truncate">{domainStatus.name}</span>
                    {domainLive ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                        <CheckCircle2 className="h-3 w-3" /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                        Waiting for DNS
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleCheck}
                    disabled={checking}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-[12px] font-semibold text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`h-3 w-3 ${checking ? 'animate-spin' : ''}`} />
                    Check status
                  </button>
                </div>

                {domainLive ? (
                  <p className="text-[13px] text-emerald-800">
                    Your domain is connected —{' '}
                    <a href={`https://${domainStatus.name}`} target="_blank" rel="noopener noreferrer" className="font-semibold underline">
                      https://{domainStatus.name}
                    </a>{' '}
                    is live.
                  </p>
                ) : (
                  <>
                    <p className="text-[13px] text-gray-700">
                      Add these records at your domain registrar (GoDaddy, Namecheap, Cloudflare, etc.), then hit{' '}
                      <span className="font-semibold">Check status</span>. DNS changes can take a few minutes to a few
                      hours to take effect.
                    </p>
                    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                      <table className="w-full text-[13px]">
                        <thead>
                          <tr className="border-b border-gray-100 text-left text-[11px] uppercase tracking-wide text-gray-400">
                            <th className="px-3 py-2 font-semibold">Type</th>
                            <th className="px-3 py-2 font-semibold">Name</th>
                            <th className="px-3 py-2 font-semibold">Value</th>
                            <th className="px-3 py-2" />
                          </tr>
                        </thead>
                        <tbody>
                          {domainStatus.dnsRecords.map((r, i) => {
                            const key = `${r.type}-${i}`;
                            return (
                              <tr key={key} className={i > 0 ? 'border-t border-gray-50' : ''}>
                                <td className="px-3 py-2 font-mono font-semibold text-gray-900">{r.type}</td>
                                <td className="px-3 py-2 font-mono text-gray-700">{r.name}</td>
                                <td className="px-3 py-2 font-mono text-gray-700 break-all">{r.value}</td>
                                <td className="px-3 py-2 text-right">
                                  <button
                                    type="button"
                                    onClick={() => copy(key, r.value)}
                                    className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-800"
                                  >
                                    {copiedKey === key ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                    {copiedKey === key ? 'Copied' : 'Copy'}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            ) : (
              /* No custom domain yet — the add field */
              <div className="rounded-xl border border-gray-200 bg-white p-3.5 space-y-2">
                <p className="text-[12px] font-semibold text-gray-700">Use your own domain</p>
                <p className="text-[12px] text-gray-500">
                  Already own a domain? Connect it and we'll show you exactly which DNS records to add.
                </p>
                <div className="flex gap-2">
                  <input
                    value={domainInput}
                    onChange={(e) => setDomainInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void handleAddDomain(); }}
                    placeholder="e.g. mybusiness.com"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  />
                  <button
                    type="button"
                    onClick={handleAddDomain}
                    disabled={busy || !domainInput.trim()}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-sm font-semibold text-white transition-colors disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
                    {busy ? 'Adding…' : 'Add domain'}
                  </button>
                </div>
              </div>
            )}

            {error && (
              <p className="text-[13px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
